import LoggerService from "@bfx/logger";
import { IActionCallout } from "app/bfx-element-library/models/CalloutModel";
import { FillNowExecutionState } from "app/bfx-order-fill-now/models/FillNowExecutionState.enum";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

const logger = LoggerService.for(__dirname);

type ActionButtons<T> = {
  submit: T;
  close: T;
  edit: T;
  accept: T;
  reject: T;
};

/**
 * Model used to represent a Fill Now operation on a single order. Contains all dynamic presentation
 * data and hols the current state of the operation.
 */
export class OrderFillNowModel {
  private orderData: CubiqTS.OrderData;

  /** Model for any callout to be shown */
  public actionCallout: IActionCallout;

  /** Enabled state of all buttons */
  public buttonEnabled: ActionButtons<boolean> = {
    submit: false,
    close: false,
    edit: false,
    accept: false,
    reject: false,
  };
  /** Visible state of all buttons */
  public buttonVisible: ActionButtons<boolean> = {
    submit: false,
    close: false,
    edit: false,
    accept: false,
    reject: false,
  };

  /** The display state of the model */
  public executionState: FillNowExecutionState = FillNowExecutionState.INIT;

  /** The amount return in the Lock operation */
  private lockOrderAmount: CubiqTS.BigNumber = null;

  public postReject: boolean = false;

  constructor(public readonly orderId: string) {
    this.reset();
  }

  /**
   * Refresh the OrderExecutionModel with a new OrderDataImpl - all models values can
   * be refreshed here
   * @param order
   */
  public set order(order: CubiqTS.OrderData) {
    if (!order) {
      logger.out.info(
        `ofn-m: Not processing update for order (${this.orderId} due to undefined input`,
      );
      return;
    }

    this.orderData = order;
  }

  public get order(): CubiqTS.OrderData {
    return this.orderData;
  }

  /**
   * Update the execution state and ensure the presentation model properties are
   * consistent.
   * If the input state is no valid as a transition, it is ignored.
   * @param state
   */
  public updateExecutionState(state: FillNowExecutionState) {
    if (!this.orderData) {
      logger.out.warn("ofn-m - Cannot update menus when no order is defined");
      return;
    }
    if (this.executionState === state) {
      logger.out.info("ofn-m - Not updating execution state - no change");
      return;
    }
    if (!this.checkStateTransition(this.executionState, state)) {
      logger.out.warn(
        `ofn-m - Not updating execution state - no legal transition from ${
          FillNowExecutionState[this.executionState]
        } to ${FillNowExecutionState[state]}`,
      );
      return;
    }

    this.buttonVisible = Object.assign({}, this.buttonVisible);

    switch (state) {
      case FillNowExecutionState.LOCKING:
        this.buttonVisible.close = false;
        this.buttonVisible.submit = false;
        this.buttonVisible.edit = false;
        this.buttonVisible.accept = false;
        this.buttonVisible.reject = false;
        break;
      case FillNowExecutionState.LOCKED:
        this.buttonEnabled.close = true;
        this.buttonVisible.close = true;
        this.buttonVisible.submit = true;
        this.buttonVisible.edit = false;

        this.buttonVisible.accept = false;
        this.buttonVisible.reject = false;
        break;
      case FillNowExecutionState.INIT:
      case FillNowExecutionState.FILLING:
        this.buttonVisible.close = false;
        this.buttonVisible.submit = false;
        this.buttonVisible.edit = false;
        this.buttonVisible.accept = false;
        this.buttonVisible.reject = false;
        break;
      case FillNowExecutionState.UNLOCKED_TERMINAL:
      case FillNowExecutionState.UNLOCKED_SUCCESS:
        this.buttonEnabled.close = true;

        this.buttonVisible.close = true;
        this.buttonVisible.submit = false;
        this.buttonVisible.edit = false;

        this.buttonVisible.accept = false;
        this.buttonVisible.reject = false;
        break;
      case FillNowExecutionState.EXT_ACCEPTANCE:
        this.buttonVisible.close = false;
        this.buttonVisible.submit = false;
        this.buttonVisible.edit = false;

        this.buttonVisible.accept = true;
        this.buttonVisible.reject = true;

        this.buttonEnabled.accept = true;
        this.buttonEnabled.reject = true;
        break;
      case FillNowExecutionState.UNLOCKED_FAIL:
      case FillNowExecutionState.UNLOCKED_EXTERNAL:
      default:
        this.buttonEnabled.close = true;
        this.buttonEnabled.edit = true;

        this.buttonVisible.close = true;
        this.buttonVisible.edit = true;
        this.buttonVisible.submit = false;

        this.buttonVisible.accept = false;
        this.buttonVisible.reject = false;
        break;
    }
    this.executionState = state;
  }

  /**
   * If we ever lose visibility of the order then we must clear the contents as they
   * are no longer visible or up-to-date
   */
  public reset() {
    this.executionState = FillNowExecutionState.INIT;
  }

  /**
   * Set the amount that is returned in the successful Lock response
   * @param value
   */
  public setLockOrderAmount(value: CubiqTS.BigNumber) {
    this.lockOrderAmount = value;
  }

  /**
   * the largest amount that can be filled
   */
  public get maxFillAmount(): CubiqTS.BigNumber {
    // For https://jira.iongroup.com/browse/FXBFXOM-875 we no longer care about the lockOrderAmount
    // return this.lockOrderAmount ?? this.orderData?.leavesQty;
    return this.orderData?.orderQty;
  }

  public get remainingFillAmount(): CubiqTS.BigNumber {
    return this.lockOrderAmount ?? this.orderData?.leavesQty;
  }

  /**
   * Ensure we can move from one state to another - if we're not in a valid state we may
   * have received a lifecycle event which will take priority
   * @param oldState
   * @param newState
   * @returns
   */
  private checkStateTransition(
    oldState: FillNowExecutionState,
    newState: FillNowExecutionState,
  ): boolean {
    let result = false;
    switch (newState) {
      case FillNowExecutionState.INIT:
        switch (oldState) {
          case null:
          case FillNowExecutionState.UNLOCKED_SUCCESS:
          case FillNowExecutionState.UNLOCKED_FAIL:
          case FillNowExecutionState.UNLOCKED_EXTERNAL:
            result = true;
            break;
        }
        break;
      case FillNowExecutionState.LOCKING:
        switch (oldState) {
          case FillNowExecutionState.INIT:
          case FillNowExecutionState.UNLOCKED_SUCCESS:
          case FillNowExecutionState.UNLOCKED_FAIL:
          case FillNowExecutionState.UNLOCKED_EXTERNAL:
            result = true;
            break;
        }
        break;
      case FillNowExecutionState.LOCKED:
        switch (oldState) {
          case FillNowExecutionState.LOCKING:
          case FillNowExecutionState.FILLING:
            result = true;
            break;
        }
        break;
      case FillNowExecutionState.FILLING:
        switch (oldState) {
          case FillNowExecutionState.LOCKED:
          case FillNowExecutionState.EXT_ACCEPTANCE:
            result = true;
            break;
        }
        break;
      case FillNowExecutionState.UNLOCKING:
        switch (oldState) {
          case FillNowExecutionState.LOCKED:
            result = true;
            break;
        }
        break;
      case FillNowExecutionState.UNLOCKED_SUCCESS:
      case FillNowExecutionState.UNLOCKED_TERMINAL:
        switch (oldState) {
          case FillNowExecutionState.FILLING:
          case FillNowExecutionState.UNLOCKING:
          case FillNowExecutionState.LOCKED:
          case FillNowExecutionState.LOCKING:
          case FillNowExecutionState.EXT_ACCEPTANCE:
            result = true;
            break;
        }
        break;
      case FillNowExecutionState.UNLOCKED_FAIL:
        switch (oldState) {
          case FillNowExecutionState.FILLING:
          case FillNowExecutionState.EXT_ACCEPTANCE:
          case FillNowExecutionState.UNLOCKING:
          case FillNowExecutionState.LOCKING:
            result = true;
            break;
        }
        break;
      case FillNowExecutionState.UNLOCKED_EXTERNAL:
        switch (oldState) {
          case FillNowExecutionState.LOCKED:
          case FillNowExecutionState.FILLING:
          case FillNowExecutionState.UNLOCKING:
            result = true;
            break;
        }
        break;
      case FillNowExecutionState.EXT_ACCEPTANCE:
        switch (oldState) {
          case FillNowExecutionState.FILLING:
            result = true;
            break;
        }
        break;
    }
    return result;
  }
}
